import React from 'react';
import NativePickerInput from '@/components/ui/NativePickerInput';

interface EventDateTimeFieldsProps {
  start: string;
  end: string;
  allDay: boolean;
  onStartChange(value: string): void;
  onEndChange(value: string): void;
  startError?: string;
  endError?: string;
}

const inputClass = (hasError: boolean) =>
  `w-full rounded-md border bg-white px-3 py-2 text-sm text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:bg-gray-800 dark:text-gray-100 ${
    hasError
      ? 'border-red-500 dark:border-red-400'
      : 'border-gray-300 dark:border-gray-600'
  }`;

const splitWallTime = (value: string): [string, string] => {
  const [date = '', time = ''] = value.split('T');
  return [date, time];
};

const combineWallTime = (date: string, time: string): string =>
  date ? `${date}T${time || '00:00'}` : '';

const EventDateTimeFields: React.FC<EventDateTimeFieldsProps> = ({
  start,
  end,
  allDay,
  onStartChange,
  onEndChange,
  startError,
  endError,
}) => {
  const [startDate, startTime] = splitWallTime(start);
  const [endDate, endTime] = splitWallTime(end);

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div>
        <label
          htmlFor="calendar-starts_at"
          className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          Start date
        </label>
        <input
          id="calendar-starts_at"
          type="date"
          value={startDate}
          aria-invalid={Boolean(startError)}
          aria-describedby={startError ? 'calendar-starts_at-error' : undefined}
          onChange={(event) =>
            onStartChange(
              combineWallTime(event.target.value, allDay ? '00:00' : startTime),
            )
          }
          className={inputClass(Boolean(startError))}
        />
        {startError && (
          <p id="calendar-starts_at-error" className="mt-1 text-sm text-red-600 dark:text-red-400">
            {startError}
          </p>
        )}
      </div>

      {!allDay && (
        <div>
          <label
            htmlFor="calendar-start-time"
            className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            Start time
          </label>
          <NativePickerInput
            id="calendar-start-time"
            type="time"
            value={startTime}
            pickerLabel="Choose start time"
            aria-invalid={Boolean(startError)}
            aria-describedby={startError ? 'calendar-starts_at-error' : undefined}
            onChange={(event) =>
              onStartChange(combineWallTime(startDate, event.target.value))
            }
            className={inputClass(Boolean(startError))}
          />
        </div>
      )}

      <div>
        <label
          htmlFor="calendar-ends_at"
          className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          End date
        </label>
        <input
          id="calendar-ends_at"
          type="date"
          value={endDate}
          aria-invalid={Boolean(endError)}
          aria-describedby={endError ? 'calendar-ends_at-error' : undefined}
          onChange={(event) =>
            onEndChange(combineWallTime(event.target.value, allDay ? '00:00' : endTime))
          }
          className={inputClass(Boolean(endError))}
        />
        {endError && (
          <p id="calendar-ends_at-error" className="mt-1 text-sm text-red-600 dark:text-red-400">
            {endError}
          </p>
        )}
      </div>

      {!allDay && (
        <div>
          <label
            htmlFor="calendar-end-time"
            className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            End time
          </label>
          <NativePickerInput
            id="calendar-end-time"
            type="time"
            value={endTime}
            pickerLabel="Choose end time"
            aria-invalid={Boolean(endError)}
            aria-describedby={endError ? 'calendar-ends_at-error' : undefined}
            onChange={(event) => onEndChange(combineWallTime(endDate, event.target.value))}
            className={inputClass(Boolean(endError))}
          />
        </div>
      )}
    </div>
  );
};

export default EventDateTimeFields;
